import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import Button from '@ui-elements/Button';
import EditButton from '@ui-elements/EditButton';

import BasicConditions from '@ui-parts/BasicConditionsArea';
import DetailedConditions from '@ui-parts/DetailedConditionsArea';
import QuotaListTable from '@ui-parts/QuotaListTable';
import QuotaListTableWithCollectCounts from '@ui-parts/QuotaListTableWithCollectCounts';
import DetailedConditionsDisplayArea from '@ui-parts/DetailedConditionsDisplayArea';
import InputValidationErrorTooltip from '@ui-parts/InputValidationErrorTooltip';

import {
  convertSpecialPanelCheckedItemsToArray,
  convertGenderToDisplayNameArray,
  convertAgeToDisplayNameArray,
  convertAreaCdsToDisplayNameArray,
  calcEnqueteCollectSum,
} from '@functional';

import GenderModal from '~/components/pages/User/Survey/organisms/GenderModal';
import AgeModal from '~/components/pages/User/Survey/organisms/AgeModal';
import QuotaListTableModal from '~/components/pages/User/Survey/organisms/QuotaListTableModal';

import { callThunk, fetchEstimatedDeliveryTargetCounts } from '~/api';
import { FONT_WEIGHTS, FONT_SIZES } from '~/assets/styles/base';
import { LOCALE_STRING, SPECIAL_PANEL_CATEGORIES } from '~/constants';

const ContentBoxTitle = styled(Typography)`
  font-weight: ${FONT_WEIGHTS.size2};
`;

const EnqueteCollectTotalWrapper = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EnqueteCollectTotal = styled.div`
  margin: 0 1rem 0 2.4rem;
  font-size: ${FONT_SIZES.body5};
  font-weight: bold;
`;

const ContentsWrapper = styled.div`
  margin: 20px 0;
`;

const QuotaListTableBox = styled.div`
  margin-top: 50px;
`;

const FlexBox = styled.div`
  display: flex;
  align-items: center;
`;

const FlexBoxBetween = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const SearchButtonContainer = styled.div`
  width: 100%;
  padding-right: 46px;
  text-align: right;
  margin-top: 16px;
  & button {
    transform: translateY(-77%);
    min-height: 32px;
  }
  & .MuiButton-startIcon {
    margin-right: 0;
    transform: translateY(7%);
  }
`;

const EditButtonContainer = styled.div`
  margin-left: 50px;
`;

const ConditionAreaWrapper = styled.div`
  margin-bottom: 20px;
  border: 1px solid #d6e0ea;
`;

// ローカルステートのエラーの初期値
const initialErrorsQualifications = {
  gender: {
    message: '',
    invalid: false,
  },
  age: {
    message: '',
    invalid: false,
  },
};

// ローカルステートのエラーの初期値
const initialErrorsQuotas = {
  quotas: {
    message: '',
    invalid: false,
  },
  targetSamples: {
    message: '',
    invalid: false,
  },
};

function Quotas({ canEdit = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const validationClickedGoToApply = useSelector((state) => state.validation.clickedGoToApply);
  const validationClickedSearchDeliveryTarget = useSelector(
    (state) => state.validation.clickedSearchDeliveryTarget
  );

  /**
   * =============
   * selector
   * =============
   */
  const survey = useSelector((state) => state.survey);
  const areasSlice = useSelector((state) => state.areas);
  const quotasSlice = useSelector((state) => state.quotas);
  const qualificationsSlice = useSelector((state) => state.qualifications);
  const collectCounts = useSelector((state) => state.quotas.collectCounts);

  /**
   * =============
   * localState
   * =============
   */
  const [openModals, setOpenModals] = useState({
    gender: false,
    age: false,
    quotas: false,
  });

  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedChoicesGender, setSelectedChoicesGender] = useState([]);

  const [targetSamples, setTargetSamples] = useState({});

  const [enqueteCollectTotal, setEnqueteCollectTotal] = useState(0);

  const [errorsQualifications, setErrorsQualifications] = useState(initialErrorsQualifications);
  const [errorsQuotas, setErrorsQuotas] = useState(initialErrorsQuotas);

  const displayConditionsCurrent = useMemo(() => {
    if (!survey.specialPanelDisplaySettingId) {
      return {};
    }
    // 固定条件である年齢・性別・地域の表示用文言を生成。
    const displayConditions = {
      age: convertAgeToDisplayNameArray(qualificationsSlice.current.age).join('、'),
      gender: convertGenderToDisplayNameArray(qualificationsSlice.current.gender).join('、'),
      region: convertAreaCdsToDisplayNameArray(
        areasSlice.areas,
        qualificationsSlice.current.areas.checkedAreaCds
      ).join('、'),
    };
    // 表示設定によって動的に変わるスペパネ条件の表示用文言を生成。
    SPECIAL_PANEL_CATEGORIES[survey.specialPanelDisplaySettingId].forEach((category) => {
      displayConditions[category] = convertSpecialPanelCheckedItemsToArray(
        qualificationsSlice.current.specialConditions.checkedItems[category],
        survey.specialPanelDisplaySettingId,
        true
      ).join('、');
    });
    return displayConditions;
  }, [qualificationsSlice.current]);

  const displayConditionsOnSearched = useMemo(() => {
    if (!survey.specialPanelDisplaySettingId) {
      return {};
    }
    // 固定条件である年齢・性別・地域の表示用文言を生成。
    const displayConditions = {
      age: convertAgeToDisplayNameArray(qualificationsSlice.onSearched.age).join('、'),
      gender: convertGenderToDisplayNameArray(qualificationsSlice.onSearched.gender).join('、'),
      region: convertAreaCdsToDisplayNameArray(
        areasSlice.areas,
        qualificationsSlice.onSearched.areas.checkedAreaCds
      ).join('、'),
    };
    // 表示設定によって動的に変わるスペパネ条件の表示用文言を生成。
    SPECIAL_PANEL_CATEGORIES[survey.specialPanelDisplaySettingId].forEach((category) => {
      displayConditions[category] = convertSpecialPanelCheckedItemsToArray(
        qualificationsSlice.onSearched.specialConditions.checkedItems[category],
        survey.specialPanelDisplaySettingId,
        true
      ).join('、');
    });
    return displayConditions;
  }, [qualificationsSlice.onSearched]);

  /**
   * =============
   * functions
   * =============
   */

  const handleClickSearch = () => {
    // validationSliceで対象者検索用バリデーションを発火させる
    dispatch({
      type: 'validation/validateOnClickedSearchDeliveryTarget',
      payload: {
        qualifications: qualificationsSlice.current,
      },
    });
  };

  /**
   * =============
   * hooks
   * =============
   */

  /**
   * グローバルステートのvalidationのstatusに依拠するhook。
   * validationが全部OKなら、対象者検索APIを呼ぶ
   * 一回目のマウント時はスキップする。
   */
  useEffect(() => {
    if (validationClickedSearchDeliveryTarget.status.valid) {
      (async () => {
        const res = await dispatch(
          callThunk({
            action: fetchEstimatedDeliveryTargetCounts,
            wholeLoadingMessage: t('quotas.loadingMessageEstimatedDeliveryTargetCounts'),
          })
        );
        if (!res.error) {
          setOpenModals({
            ...openModals,
            quotas: true,
          });
        }
        // validationエラーを全て初期化
        dispatch({
          type: 'validation/validationInitialized',
          payload: {
            eventType: 'all',
          },
        });
      })();
    }
  }, [validationClickedSearchDeliveryTarget.status]);

  useEffect(() => {
    setErrorsQualifications(validationClickedGoToApply.errors.qualifications);
  }, [validationClickedGoToApply.errors.qualifications]);

  useEffect(() => {
    setErrorsQuotas(validationClickedGoToApply.errors.quotas);
  }, [validationClickedGoToApply.errors.quotas]);

  useEffect(() => {
    setErrorsQualifications(validationClickedSearchDeliveryTarget.errors.qualifications);
  }, [validationClickedSearchDeliveryTarget.errors.qualifications]);

  useEffect(() => {
    setTargetSamples(quotasSlice.targetSamples);
    setEnqueteCollectTotal(calcEnqueteCollectSum(collectCounts));
  }, [quotasSlice.targetSamples]);

  useEffect(() => {
    setSelectedChoicesGender(convertGenderToDisplayNameArray(qualificationsSlice.current.gender));
  }, [qualificationsSlice.current.gender]);

  useEffect(() => {
    setSelectedAges(convertAgeToDisplayNameArray(qualificationsSlice.current.age));
  }, [qualificationsSlice.current.age]);

  return (
    <>
      <ContentBoxTitle variant="title2">
        {canEdit ? t('quotas.searchDeliveryTarget') : t('quotas.deliveryTargetCondition')}
      </ContentBoxTitle>

      <ContentsWrapper>
        {canEdit ? (
          <>
            <BasicConditions
              conditions={{
                gender: {
                  selectedChoices: selectedChoicesGender,
                  handleClickAddBtn: () => {
                    setOpenModals({
                      ...openModals,
                      gender: true,
                    });
                  },
                  handleClickEditBtn: () => {
                    setOpenModals({
                      ...openModals,
                      gender: true,
                    });
                  },
                  editable: selectedChoicesGender.length > 0,
                },
                age: {
                  selectedChoices: selectedAges,
                  handleClickAddBtn: () => {
                    setOpenModals({
                      ...openModals,
                      age: true,
                    });
                  },
                  handleClickEditBtn: () => {
                    setOpenModals({
                      ...openModals,
                      age: true,
                    });
                  },
                  editable: selectedAges.length > 0,
                },
              }}
              errors={errorsQualifications}
            />
            {survey.specialPanelDisplaySettingId && (
              <DetailedConditions
                areas={areasSlice.areas}
                checkedAreaCds={qualificationsSlice.current.areas.checkedAreaCds}
                checkedItems={qualificationsSlice.current.specialConditions.checkedItems}
                specialPanelDisplaySettingId={survey.specialPanelDisplaySettingId}
              />
            )}
            <SearchButtonContainer>
              <Button
                variant="contained"
                handleClick={handleClickSearch}
                startIcon={<SearchRoundedIcon />}
                width="140px"
                data-testid="Survey-Organisms-Quotes-handleClickSearch"
              >
                {t('quotas.searchDeliveryTargetButton')}
              </Button>
            </SearchButtonContainer>
          </>
        ) : (
          <ConditionAreaWrapper>
            <DetailedConditionsDisplayArea conditions={displayConditionsCurrent} />
          </ConditionAreaWrapper>
        )}
      </ContentsWrapper>

      <QuotaListTableBox>
        {canEdit ? (
          <FlexBox>
            {errorsQuotas.targetSamples && errorsQuotas.targetSamples.invalid ? (
              <InputValidationErrorTooltip
                error={errorsQuotas.targetSamples.invalid}
                message={errorsQuotas.targetSamples.message}
                dataTestId="TargetSamples"
              >
                <ContentBoxTitle variant="title2">
                  {t('quotas.targetSamplesTotalBreakdown')}
                </ContentBoxTitle>
              </InputValidationErrorTooltip>
            ) : (
              <ContentBoxTitle variant="title2">
                {t('quotas.targetSamplesTotalBreakdown')}
              </ContentBoxTitle>
            )}
            {Object.keys(quotasSlice.quotas).length > 0 && (
              <EditButtonContainer>
                <EditButton
                  sx={{ marginLeft: 26 }}
                  handleClick={() => {
                    setOpenModals({
                      ...openModals,
                      quotas: true,
                    });
                  }}
                  name="TargetSamplesTotalBreakdown"
                >
                  <EditRoundedIcon />
                </EditButton>
              </EditButtonContainer>
            )}
          </FlexBox>
        ) : (
          <FlexBoxBetween>
            <ContentBoxTitle variant="title2">{t('quotas.currentEnqueteCounts')}</ContentBoxTitle>
            <EnqueteCollectTotalWrapper variant="title2">
              <div>{t('quotas.enqueteCollectTotalCounts')}</div>
              <EnqueteCollectTotal>
                {enqueteCollectTotal.toLocaleString(LOCALE_STRING.ja)}
              </EnqueteCollectTotal>
            </EnqueteCollectTotalWrapper>
          </FlexBoxBetween>
        )}
        <ContentsWrapper>
          {canEdit ? (
            <QuotaListTable
              ages={quotasSlice.ages}
              quotas={quotasSlice.quotas}
              targetSamples={targetSamples}
              qualificationsGender={qualificationsSlice.onSearched.gender}
              errors={errorsQuotas}
            />
          ) : (
            <QuotaListTableWithCollectCounts
              ages={quotasSlice.ages}
              enqueteCollectCounts={collectCounts}
              targetSamples={targetSamples}
              qualificationsGender={qualificationsSlice.current.gender}
            />
          )}
        </ContentsWrapper>
      </QuotaListTableBox>

      <GenderModal
        open={openModals.gender}
        handleClose={() => {
          setOpenModals({
            ...openModals,
            gender: false,
          });
        }}
      />

      <AgeModal
        open={openModals.age}
        handleClose={() => {
          setOpenModals({
            ...openModals,
            age: false,
          });
        }}
      />

      <QuotaListTableModal
        open={useMemo(() => openModals.quotas, [openModals.quotas])}
        handleClose={useCallback(() => {
          setOpenModals({
            ...openModals,
            quotas: false,
          });
        }, [openModals])}
        conditions={displayConditionsOnSearched}
      />
    </>
  );
}

Quotas.propTypes = {
  canEdit: PropTypes.bool,
};

export default Quotas;
